import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Divider, Input, message, Modal, Row, Typography } from 'antd';
import { capitalize, get } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import ResellerServices from 'services/reseller';

import PdButton from 'components/pd-button';

import { errHandling } from 'utils';

const { confirm } = Modal;
const { Text, Title } = Typography;
const { TextArea } = Input;

const DETAILS = [
  { TITLE: 'First Name', KEY: 'first_name' },
  { TITLE: 'Last Name', KEY: 'last_name' },
  { TITLE: 'Phone Number', KEY: 'phone_number' },
  { TITLE: 'NIK', KEY: 'nik_number' },
  { TITLE: 'Type', KEY: 'reseller_type' },
  { TITLE: 'Email', KEY: 'email' },
  { TITLE: 'Store Name', KEY: 'store_name' },
  { TITLE: 'Gender', KEY: 'gender' },
  { TITLE: 'Date of Birth', KEY: 'date_of_birth' },
];

const BANK = [
  { TITLE: 'Bank Name', KEY: 'bank_name' },
  { TITLE: 'Bank Branch', KEY: 'bank_branch' },
  { TITLE: 'Account Number', KEY: 'account_number' },
  { TITLE: 'Account Holder', KEY: 'account_holder' },
];

const Informations = ({ accepted = false, id = '', value = {}, bank = {} }) => {
  const [reason, setReason] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [resellerCode, setResellerCode] = useState('');

  const { application_status = '', first_name = '', last_name = '' } = value;
  const navigate = useNavigate();
  const showBtn = application_status === 'applied';

  function actionHandler(action) {
    switch (action) {
      case 'dealer':
        confirm({
          title: `Accept as Dealer`,
          content: `Accept ${first_name} ${last_name} as Dealer Reseller?`,
          okText: 'Accept',
          cancelText: 'Cancel',
          onOk() {
            acceptReseller('dealer');
          },
        });
        break;
      case 'individual':
        confirm({
          title: `Accept as Individual`,
          content: `Accept ${first_name} ${last_name} as Individual Reseller?`,
          okText: 'Accept',
          cancelText: 'Cancel',
          onOk() {
            acceptReseller('individual');
          },
        });
        break;
      case 'reject':
        setShowModal(true);
        break;
      default:
        break;
    }
  }

  async function acceptReseller(type) {
    try {
      await ResellerServices.acceptReseller(id, type);
      message.success(`Reseller has been accept as ${type}`);
      navigate('/resellers_status');
    } catch (error) {
      errHandling(error);
    }
  }

  async function rejectReseller() {
    try {
      await ResellerServices.rejectReseller(id, reason);
      message.success(`Reseller has been rejected`);
      navigate('/resellers_status');
    } catch (error) {
      errHandling(error);
    }
  }

  function closeModal() {
    setShowModal(false);
    setReason('');
  }

  function formatValue(values, key) {
    if (['nik_number'].includes(key)) {
      const val = get(values.business_application, key, '');
      return val;
    }
    const val = get(values, key, '');
    if (['first_name', 'last_name', 'reseller_type', 'gender'].includes(key))
      return capitalize(val);
    else if (key === 'date_of_birth') return dayjs(val).format('DD-MM-YYYY');
    else return val;
  }

  function handleChangeReason(e) {
    setReason(e.target.value);
  }

  async function updateResellerCode() {
    try {
      if (resellerCode.length < 6)
        return message.error('Reseller code must be at least 6 characters');
      await ResellerServices.updateResellerCode(value.id, resellerCode);
      message.success(`Reseller code has been updated`);
      navigate('/resellers_status');
    } catch (error) {
      errHandling(error);
    }
  }

  return (
    <Col span={24} className="section-container">
      <div className=" inner-section">
        <Row className="summaryContent">
          <Col span={24}>
            <Title className="pageSubtitle" level={3}>
              Reseller Info
            </Title>
          </Col>
          <Col span={18}>
            <Row>
              {DETAILS.map((DETAIL) => (
                <Col span={24} className="mb-15" key={DETAIL.KEY}>
                  <Col span={7}>
                    <Text strong>{DETAIL.TITLE}</Text>
                  </Col>
                  <Col span={17}>
                    <Text type="secondary">
                      {formatValue(value, DETAIL.KEY)}
                    </Text>
                  </Col>
                </Col>
              ))}
              {BANK.map((DETAIL) => (
                <Col span={24} className="mb-15" key={DETAIL.KEY}>
                  <Col span={7}>
                    <Text strong>{DETAIL.TITLE}</Text>
                  </Col>
                  <Col span={17}>
                    <Text type="secondary">
                      {formatValue(bank, DETAIL.KEY)}
                    </Text>
                  </Col>
                </Col>
              ))}
            </Row>
          </Col>

          {!accepted && showBtn && (
            <Col span={6}>
              <div className="buttonSection">
                <PdButton
                  className="button-detail"
                  onClick={actionHandler.bind(this, 'dealer')}
                  type="primary">
                  Accept as Dealer
                </PdButton>
                <PdButton
                  className="button-detail"
                  onClick={actionHandler.bind(this, 'individual')}
                  type="primary">
                  Accept as Individual
                </PdButton>
                <PdButton
                  className="button-detail"
                  onClick={actionHandler.bind(this, 'reject')}
                  type="danger">
                  Reject Application
                </PdButton>
              </div>
            </Col>
          )}

          {accepted && (
            <Col span={6}>
              <div className="buttonSection">
                <PdButton
                  className="button-detail"
                  onClick={() => {
                    setShowEditModal(true);
                  }}
                  type="primary">
                  Edit Reseller Code
                </PdButton>
              </div>
            </Col>
          )}
        </Row>

        <Modal
          footer={null}
          onCancel={closeModal}
          title="Rejection Reason"
          open={showModal}>
          <Row type="flex" justify="center" align="middle">
            <Col span={24} className="mt-2">
              <TextArea
                rows={4}
                placeholder="Enter reasons here"
                onChange={handleChangeReason}
              />
            </Col>

            <Col span={24}>
              <div className="buttonSection buttonSection-row mt-2">
                <PdButton
                  disabled={reason ? false : true}
                  onClick={rejectReseller}
                  type="primary">
                  Save
                </PdButton>
                <PdButton
                  className="pd-margin-left-md"
                  onClick={closeModal}
                  type="danger">
                  Cancel
                </PdButton>
              </div>
            </Col>
          </Row>
        </Modal>

        <Modal
          footer={null}
          onCancel={() => {
            setShowEditModal(false);
            setResellerCode('');
          }}
          title="Edit Reseller Code"
          open={showEditModal}>
          <Row gutter={[12, 12]} align="middle">
            <Col span={12}>Reseller Name</Col>
            <Col span={12}>
              {value.first_name} {value.last_name}
            </Col>
            <Col span={12}>Old Reseller Code</Col>
            <Col span={12}>
              <Input
                placeholder="reseller code"
                value={value.referral_code}
                disabled
              />
            </Col>
            <Col span={12}>New Reseller Code</Col>
            <Col span={12}>
              <Input
                placeholder="reseller code"
                value={resellerCode}
                onChange={(e) => setResellerCode(e.target.value)}
              />
            </Col>
          </Row>
          <Divider />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <PdButton
              onClick={() => {
                setShowEditModal(false);
                setResellerCode('');
              }}
              type="danger">
              Cancel
            </PdButton>
            <PdButton onClick={updateResellerCode} type="primary">
              Save Changes
            </PdButton>
          </div>
        </Modal>
      </div>
    </Col>
  );
};

export default Informations;
